import type {
  LensPresets,
  LensTheme,
} from '@diligentcorp/atlas-theme-mui-lens';
import { Box, CircularProgress, Drawer, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { ReactNode } from 'react';
import emptyStateIllustration from 'src/components/CategoryConfiguration/assets/atlas-empty-state.svg';
import {
  AuditLogColumn,
  AuditLogPaginatedTable,
} from 'src/components/atlas/molecules/AuditLogPaginatedTable';
import { EmptyState } from 'src/components/atlas/molecules/EmptyState';
import {
  AuditEntryType,
  useGetCaseDataDownloadAuditEntriesQuery,
} from 'src/graphql';

export interface CasesAuditLogDrawerProps {
  open: boolean;
  onClose: () => void;
}

const COLUMNS: AuditLogColumn[] = [
  { label: 'User' },
  { label: 'Event' },
  { label: 'Timestamp' },
];

const PAGE_SIZE = 10;

const EMPTY_STATE = (
  <EmptyState
    title="No events logged"
    description="There are no events logged yet"
    illustration={
      <img
        src={emptyStateIllustration}
        alt=""
        aria-hidden="true"
        data-testid="empty-state-illustration"
        width={144}
        height={144}
      />
    }
  />
);

const ERROR_STATE = (
  <EmptyState
    title="Couldn't load audit log"
    description="Try closing and reopening the panel. If this keeps happening, contact support."
  />
);

const getUserName = (entry: AuditEntryType): string =>
  entry.User?.name ?? entry.User?.displayName ?? 'System';

const formatTimestamp = (iso: string): string =>
  dayjs(iso).format('YYYY-MM-DD HH:mm');

export const CasesAuditLogDrawer: React.FC<CasesAuditLogDrawerProps> = ({
  open,
  onClose,
}) => {
  const { presets } = useTheme<LensTheme>();
  const SideSheetPresets = presets?.SideSheetPresets;
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={SideSheetPresets ? { ...SideSheetPresets.size.large.sx } : undefined}
      data-testid="audit-log-drawer"
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': 'audit-log-title',
        },
      }}
    >
      {open ? (
        <DrawerBody onClose={onClose} SideSheetPresets={SideSheetPresets} />
      ) : null}
    </Drawer>
  );
};

type SideSheetPresetsShape = LensPresets['SideSheetPresets'];

interface DrawerBodyProps {
  onClose: () => void;
  SideSheetPresets?: SideSheetPresetsShape;
}

const DrawerBody: React.FC<DrawerBodyProps> = ({
  onClose,
  SideSheetPresets,
}) => {
  const { data, isLoading, isError } =
    useGetCaseDataDownloadAuditEntriesQuery();

  const entries: AuditEntryType[] =
    (data?.caseDataDownloadAuditEntries as AuditEntryType[] | undefined) ?? [];

  const renderRow = (entry: AuditEntryType): ReactNode[] => [
    getUserName(entry),
    'Case data downloaded',
    entry.createdAt ? formatTimestamp(entry.createdAt) : '',
  ];

  let body: ReactNode;
  if (isLoading) {
    body = (
      <Box
        data-testid="audit-log-loading"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  } else if (isError) {
    body = ERROR_STATE;
  } else {
    body = (
      <AuditLogPaginatedTable
        entries={entries}
        columns={COLUMNS}
        renderRow={renderRow}
        pageSize={PAGE_SIZE}
        emptyState={EMPTY_STATE}
        ariaLabel="Cases audit log"
      />
    );
  }

  const Header = SideSheetPresets?.components?.Header;
  const Content = SideSheetPresets?.components?.Content;

  return (
    <>
      {Header ? (
        <Header
          variant="default"
          onClose={onClose}
          title="Audit log"
          componentProps={{
            closeButton: { 'aria-label': 'Close audit log' },
            title: { component: 'h2', id: 'audit-log-title' },
          }}
        />
      ) : null}
      {Content ? (
        <Content ariaLabel="Audit log entries">{body}</Content>
      ) : (
        <Box>{body}</Box>
      )}
    </>
  );
};
